import React from "react";
import get from "lodash/get";
import map from "lodash/map";
import forEach from "lodash/forEach";
import { connect } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Col,
  Form,
  Icon,
  Input,
  InputNumber,
  message,
  Row,
  Select,
} from "antd";
import { withNamespaces } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import classes from "./WizardConfig.css";

import { postDrugManagementConfig } from "../../api/requests";
import { logout, setAppSettings } from "../../reducers/user/actions";

const { Option } = Select;

const CLIENT_WEIGHT = 80;

class DrugManagementConfigForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      configData: {},
      isLoadingData: false,
      isLoading: false,
      drugDosageSettingsKeys: [],
      drugDosageSettingsValues: {},
      randomizationData: null,
      errorMessage: null,
    };
  }

  componentDidMount() {
    this.setData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.drugManagementData.is_configured !==
      this.props.drugManagementData.is_configured
    ) {
      this.setData();
    }
  }

  setData = () => {
    const { randomizationData, drugManagementData } = this.props;

    const drugDosageSettingsKeys =
      drugManagementData.drug_dosage_settings &&
      drugManagementData.drug_dosage_settings.length > 0
        ? [...map(drugManagementData.drug_dosage_settings, () => uuidv4())]
        : [uuidv4()];

    const drugDosageSettingsValues = {};
    forEach(drugDosageSettingsKeys, (key, i) => {
      if (
        drugManagementData.drug_dosage_settings &&
        drugManagementData.drug_dosage_settings[i]
      ) {
        drugDosageSettingsValues[key] = {
          ...drugManagementData.drug_dosage_settings[i],
        };
      }
    });

    this.setState({
      randomizationData,
      configData: drugManagementData,
      drugDosageSettingsKeys,
      drugDosageSettingsValues,
    });
  };

  removeDrugDosageGroup = (key) => {
    const drugDosageSettingsKeys = [
      ...this.state.drugDosageSettingsKeys,
    ].filter((k) => key !== k);

    this.setState({
      drugDosageSettingsKeys,
    });
  };

  addDrugDosageGroup = () => {
    this.setState({
      drugDosageSettingsKeys: [...this.state.drugDosageSettingsKeys, uuidv4()],
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({
      errorMessage: null,
    });
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const data = { ...values };
        delete data["drugDosageGroup"];
        delete data["drug_shipment_amount_active"];
        data["drug_dosage_settings"] = [];
        data["drug_shipment_amount"] = values.drug_shipment_amount || 0;

        if (values["drugDosageGroup"]) {
          Object.keys(values["drugDosageGroup"]).map((key) => {
            const group = values["drugDosageGroup"][key];
            group.maximum_dosage_weight =
              values["drugDosageGroup"][key].maximum_dosage_weight || null;
            group.maximum_dosage_amount =
              values["drugDosageGroup"][key].maximum_dosage_amount || null;
            group.minimum_dosage_weight =
              values["drugDosageGroup"][key].minimum_dosage_weight || null;
            group.minimum_dosage_amount =
              values["drugDosageGroup"][key].minimum_dosage_amount || null;

            data["drug_dosage_settings"].push(group);
          });
        }

        try {
          await postDrugManagementConfig(data);
          message.success(this.props.i18n.t("Saved"));
          this.props.setAppSettings();
          this.props.onSubmit();
        } catch (e) {
          this.setState({
            errorMessage: JSON.stringify(get(e, "response.data.errors")),
          });
          message.error(this.props.i18n.t("Error"));
          console.log(e);
        }
      }
    });
  };

  getRepeatDescription = (repeat_frequency_hours, interval_repeat_times) => {
    if (repeat_frequency_hours && interval_repeat_times) {
      let descr;
      let total_hours = repeat_frequency_hours * interval_repeat_times;
      descr = `${total_hours} hours`;

      if (total_hours) {
        if (total_hours % 24 === 0) {
          descr = `${Number.parseInt(total_hours / 24)} days`;
        }

        if (total_hours === 24) {
          descr = `1 day`;
        }

        if (total_hours % 168 === 0) {
          descr = `${Number.parseInt(total_hours / 168)} weeks`;
        }

        if (total_hours === 168) {
          descr = `${Number.parseInt(total_hours / 168)} week`;
        }
      }

      return `repeat every ${repeat_frequency_hours} hours for ${descr}`;
    } else {
      return "";
    }
  };

  accountMinMaxWeightDosage = (
    _weight,
    maximum_dosage_weight,
    minimum_dosage_weight,
    dose_per_kg
  ) => {
    if (_weight > maximum_dosage_weight) {
      return maximum_dosage_weight * dose_per_kg;
    }

    if (_weight < minimum_dosage_weight) {
      return minimum_dosage_weight * dose_per_kg;
    }
  };

  getDosageInstructions(unit, dose_per_kg, drug_interval_hours = null) {
    if (!unit || !dose_per_kg) {
      return "";
    }

    if (drug_interval_hours) {
      return `${dose_per_kg}/${unit}/${drug_interval_hours}hrs`;
    }

    return `${dose_per_kg}/${unit}`;
  }

  calculateLiquidDose = ({
    additional_instructions,
    drug_administration_type,
    calculate_based_on_weight,
    bolus_dose_mg_per_kg,
    fixed_dose_amount,
    infusion_dose_ml_per_kg,
    maximum_dosage_weight,
    minimum_dosage_weight,
  }) => {
    let descr;
    const result = {
      administration_instructions: "",
      dose_amount: 0,
    };

    if (drug_administration_type === "bolus") {
      result["dose_unit_type"] = "mg";

      if (calculate_based_on_weight) {
        result["bolus_dose"] = this.accountMinMaxWeightDosage(
          CLIENT_WEIGHT,
          maximum_dosage_weight,
          minimum_dosage_weight,
          bolus_dose_mg_per_kg
        );

        if (!result["bolus_dose"]) {
          result["bolus_dose"] = bolus_dose_mg_per_kg * CLIENT_WEIGHT;
        }
      } else {
        result["bolus_dose"] = fixed_dose_amount;
      }

      result["dose_amount"] = result["bolus_dose"];
    } else {
      if (drug_administration_type === "infusion") {
        result["dose_unit_type"] = "ml";

        if (calculate_based_on_weight) {
          result["infusion_dose"] = this.accountMinMaxWeightDosage(
            CLIENT_WEIGHT,
            maximum_dosage_weight,
            minimum_dosage_weight,
            infusion_dose_ml_per_kg
          );

          if (!result["infusion_dose"]) {
            result["infusion_dose"] = infusion_dose_ml_per_kg * CLIENT_WEIGHT;
          }
        } else {
          result["infusion_dose"] = fixed_dose_amount;
        }

        result["dose_amount"] = result["infusion_dose"];
      }
    }

    if (result.dose_amount) {
      descr =
        drug_administration_type === "infusion" ? "infusion" : "injection";
      result[
        "administration_instructions"
      ] = `${descr} ${result["dose_amount"]} ${result["dose_unit_type"]}`;
    }

    if (additional_instructions) {
      result["administration_instructions"] += " " + additional_instructions;
    }

    result["dosage_instructions"] = this.getDosageInstructions(
      result["dose_unit_type"],
      result["dose_amount"]
    );
    return result;
  };

  getAdministrationSummary = (key) => {
    const { drugDosageGroup } = this.props.form.getFieldsValue();
    const {
      additional_instructions,
      drug_administration_type,
      infusion_dose_ml_per_kg,
      bolus_dose_mg_per_kg,
      calculate_based_on_weight,
      dosage_amount_per_kg,
      calculate_based_on_time,
      active_ingredient_interval_hours,
      repeat_frequency_hours,
      interval_repeat_times,
      fixed_dose_amount,
      maximum_dosage_weight,
      minimum_dosage_weight,
    } = drugDosageGroup[key];

    const liquidResult = this.calculateLiquidDose({
      additional_instructions,
      drug_administration_type,
      calculate_based_on_weight,
      bolus_dose_mg_per_kg,
      fixed_dose_amount,
      infusion_dose_ml_per_kg,
      maximum_dosage_weight,
      minimum_dosage_weight,
    });

    return (
      <div>
        {["infusion", "bolus"].includes(drug_administration_type) ? (
          <div>
            {liquidResult.administration_instructions && (
              <p>{liquidResult.administration_instructions}</p>
            )}
          </div>
        ) : calculate_based_on_weight ? (
          <p>
            {dosage_amount_per_kg
              ? `${dosage_amount_per_kg * 80} mg ${
                  calculate_based_on_time
                    ? `${
                        active_ingredient_interval_hours
                          ? `over ${active_ingredient_interval_hours} hours, `
                          : ""
                      } ${this.getRepeatDescription(
                        repeat_frequency_hours,
                        interval_repeat_times
                      )}`
                    : "one time"
                }`
              : ""}
          </p>
        ) : (
          <p>
            {fixed_dose_amount &&
              `${fixed_dose_amount} mg ${
                calculate_based_on_time
                  ? `${
                      active_ingredient_interval_hours
                        ? `over ${active_ingredient_interval_hours} hours, `
                        : ""
                    } ${this.getRepeatDescription(
                      repeat_frequency_hours,
                      interval_repeat_times
                    )}`
                  : "one time"
              }`}
          </p>
        )}
      </div>
    );
  };

  render() {
    const {
      configData,
      isLoading,
      isLoadingData,
      drugDosageSettingsKeys,
      errorMessage,
    } = this.state;
    const { randomizationData, isDisabled } = this.props;
    const { getFieldDecorator, getFieldValue } = this.props.form;
    const isRandomizeConfig =
      get(randomizationData, "treatment_groups", []).length > 0;
    const isEditable = isRandomizeConfig;

    let drugUploadLocation = "";
    let drugAvailableAtLocation = "";

    if (getFieldValue(`manufacturer_site_active`)){
      drugUploadLocation = this.props.i18n.t("Manufacturer");
    }else if (getFieldValue(`depot_site_active`)){
      drugUploadLocation = this.props.i18n.t("Depot");
    }
    else if (getFieldValue(`pharmacy_site_active`)){
      drugUploadLocation = this.props.i18n.t("Pharmacy");
    }
    else if (getFieldValue(`fridge_site_active`)){
      drugUploadLocation = this.props.i18n.t("Fridge");
    }

    if (getFieldValue(`fridge_site_active`)){
      drugAvailableAtLocation = this.props.i18n.t("Fridge");
    }
    else if (getFieldValue(`pharmacy_site_active`)){
      drugAvailableAtLocation = this.props.i18n.t("Pharmacy");
    }
    else if (getFieldValue(`depot_site_active`)){
      drugAvailableAtLocation = this.props.i18n.t("Depot");
    }
    else if (getFieldValue(`manufacturer_site_active`)){
      drugAvailableAtLocation = this.props.i18n.t("Manufacturer");
    }

    return (
      <div className={classes.formWrapper}>
        <h3 className="headline-default">{this.props.i18n.t(
                      "NamespaceWizardConfig:Drug Management Config"
                    )} </h3>

        {!isLoadingData ? (
          <Form onSubmit={this.handleSubmit}>
            <Row gutter={8}>
              <Col sm={24}>
                {!isRandomizeConfig && (
                  <Alert
                    type="warning"
                    message={this.props.i18n.t(
                      "NamespaceWizardConfig:Please fill Randomization Config first"
                    )}
                  />
                )}

                <div style={{ margin: "18px 0 20px" }}>
                  {getFieldDecorator("drug_management_active", {
                    initialValue: get(
                      configData,
                      "drug_management_active",
                      false
                    ),
                    valuePropName: "checked",
                  })(
                    <Checkbox disabled={!isEditable || isDisabled}>
                      <span style={{ fontSize: "18px", fontWeight: 600 }}>
                        {this.props.i18n.t("NamespaceWizardConfig:Drug management active")}
                      </span>
                    </Checkbox>
                  )}
                </div>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col sm={24}>
                <div style={{ margin: "0 0 20px" }}>
                  {getFieldDecorator("drug_shipment_amount_active", {
                    initialValue: !!get(
                      configData,
                      "drug_shipment_amount",
                      false
                    ),
                    valuePropName: "checked",
                  })(
                    <Checkbox disabled={!isEditable || isDisabled}>
                      <span style={{ fontSize: "18px", fontWeight: 600 }}>
                        {this.props.i18n.t("NamespaceWizardConfig:Default drug shipment amount")}
                      </span>
                    </Checkbox>
                  )}
                </div>
              </Col>

              <Col sm={16}>
                <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Drug balancing")}>
                  {getFieldDecorator(`drug_balancing`, {
                    initialValue: get(configData, `drug_balancing`, null),
                    rules: [
                      {
                        required: true,
                        message: this.props.i18n.t("Required"),
                      },
                    ],
                  })(
                    <Select
                      disabled={!isEditable || isDisabled}
                      placeholder={this.props.i18n.t("Select")}
                    >
                      <Option value="balancing_within_shipment">
                        {this.props.i18n.t("NamespaceWizardConfig:Balance within shipment only")}
                      </Option>
                      <Option
                        disabled
                        value="balancing_availability_destination"
                      >
                        {this.props.i18n.t("N/A")}
                      </Option>
                    </Select>
                  )}
                </Form.Item>

                {getFieldValue(`drug_balancing`) ===
                  "balancing_within_shipment" && (
                  <div>
                    <ul>
                      <li>
                        {this.props.i18n.t(`NamespaceWizardConfig:this means that we ensure the correct ratio of drugs is used in the shipment only`)}
                      </li>
                      <li>
                        {this.props.i18n.t(`NamespaceWizardConfig:e.g., if 2 drug types (active/control) exist and they
                        have a 50/50 ratio in the wizard config, then when we
                        ship 6 drugs, the system should choose 3 active and 3
                        control`)}
                      </li>
                    </ul>
                  </div>
                )}

                {getFieldValue(`drug_balancing`) ===
                  "balancing_availability_destination" && (
                  <div>
                    <ul>
                      <li>
                        {this.props.i18n.t(`NamespaceWizardConfig:this means that we ensure that the correct ratio of
                        drugs will be select to ensure the destination location
                        has a balanced ratio of drugs`)}
                      </li>
                      <li>
                        {this.props.i18n.t(`NamespaceWizardConfig:e.g., if 2 drug types (active/control) exist and they
                        have a 50/50 ratio in the wizard config, and the
                        destination has 2 active and 4 control available, if we
                        select to ship 6 drugs, then the system should select 4
                        active and 2 control (so that we end up with a 50/50
                        balance at the destination)`)}
                      </li>
                      <li>
                        {this.props.i18n.t(`NamespaceWizardConfig:calculation - step 1 - calculate imbalance (4 - 2) = 2`)}
                      </li>
                      <li>
                        {this.props.i18n.t(`NamespaceWizardConfig:calculation - step 2 - check if new shipment amount is
                        higher than imbalance`)}
                      </li>
                      <li>
                        {this.props.i18n.t(`NamespaceWizardConfig:calculation - step 3 - if shipment higher, than add
                        towards the imbalance first and then split the rest
                        based on the ration (e.g., 50/50)`)}
                      </li>
                    </ul>
                  </div>
                )}
              </Col>
            </Row>
            <Row gutter={8}>
              {getFieldValue(`drug_shipment_amount_active`) && (
                <Col lg={8}>
                  <Form.Item label={this.props.i18n.t("Drug shipment amount")}>
                    <div>
                      {getFieldDecorator("drug_shipment_amount", {
                        initialValue: get(
                          configData,
                          "drug_shipment_amount",
                          null
                        ),
                        rules: [
                          {
                            required: true,
                            message: this.props.i18n.t("Required"),
                          },
                        ],
                      })(
                        <InputNumber
                          disabled={!isEditable || isDisabled}
                          placeholder={this.props.i18n.t(
                            "NamespaceWizardConfig:Drug shipment amount"
                          )}
                          allowClear
                        />
                      )}
                    </div>
                  </Form.Item>
                </Col>
              )}
            </Row>
            <Row gutter={8}>
              <Col lg={8}>
                <Form.Item
                  label={this.props.i18n.t("NamespaceWizardConfig:Minimum subject weight (Optional)")}
                >
                  <div>
                    {getFieldDecorator("minimum_subject_weight", {
                      initialValue: get(
                        configData,
                        "minimum_subject_weight",
                        null
                      ),
                    })(
                      <Input
                        type="number"
                        disabled={!isEditable || isDisabled}
                        placeholder={this.props.i18n.t(
                          "NamespaceWizardConfig:Minimum subject weight"
                        )}
                        addonAfter={this.props.i18n.t("NamespaceWizardConfig:kg")}
                        allowClear
                      />
                    )}
                  </div>
                </Form.Item>
              </Col>
              <Col lg={8}>
                <Form.Item
                  label={this.props.i18n.t("NamespaceWizardConfig:Maximum subject weight (Optional)")}
                >
                  <div>
                    {getFieldDecorator("maximum_subject_weight", {
                      initialValue: get(
                        configData,
                        "maximum_subject_weight",
                        null
                      ),
                    })(
                      <Input
                        type="number"
                        disabled={!isEditable || isDisabled}
                        placeholder={this.props.i18n.t(
                          "NamespaceWizardConfig:Maximum subject weight"
                        )}
                        addonAfter={this.props.i18n.t("NamespaceWizardConfig:kg")}
                        allowClear
                      />
                    )}
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <div style={{ marginBottom: "15px" }}>
              <Row gutter={8}>
                <Col sm={24}>
                  <div className={classes.drugDosageGroup}>
                    {drugDosageSettingsKeys.map((key, i) =>
                      key ? (
                        <div className={classes.itemGroup} key={i}>
                          <div style={{ fontSize: "16px" }}>
                            <b>Group #{i + 1}</b>
                            {drugDosageSettingsKeys.length > 1 && (
                              <Icon
                                style={{
                                  margin: "3px 0 5px 10px",
                                  color: "#f00",
                                }}
                                type="delete"
                                onClick={() => {
                                  this.removeDrugDosageGroup(key);
                                }}
                              />
                            )}
                          </div>
                          <Row gutter={8}>
                            <Col lg={8}>
                              <div>
                                <Form.Item
                                  label={this.props.i18n.t("NamespaceWizardConfig:Treatment group")}
                                >
                                  {getFieldDecorator(
                                    `drugDosageGroup[${key}].treatment_group`,
                                    {
                                      initialValue: get(
                                        this.state,
                                        `drugDosageSettingsValues[${key}].treatment_group`,
                                        null
                                      ),
                                      rules: [
                                        {
                                          required: true,
                                          message: this.props.i18n.t("Required"),
                                        },
                                      ],
                                    }
                                  )(
                                    <Select
                                      disabled={!isEditable || isDisabled}
                                      placeholder={this.props.i18n.t("Select")}
                                    >
                                      <Option value="_default_">{this.props.i18n.t("All")}</Option>
                                      {map(
                                        get(
                                          randomizationData,
                                          "treatment_groups",
                                          []
                                        ),
                                        ({ type }, i) => (
                                          <Option value={type} key={i}>
                                            {type}
                                          </Option>
                                        )
                                      )}
                                    </Select>
                                  )}
                                </Form.Item>
                              </div>
                            </Col>

                            <Col lg={8}>
                              <div>
                                <Form.Item
                                  label={this.props.i18n.t("NamespaceWizardConfig:Drug name")}
                                >
                                  {getFieldDecorator(
                                    `drugDosageGroup[${key}].drug_name`,
                                    {
                                      initialValue: get(
                                        this.state,
                                        `drugDosageSettingsValues[${key}].drug_name`,
                                        null
                                      ),
                                      rules: [
                                        {
                                          required: true,
                                          message: this.props.i18n.t("Required"),
                                        },
                                      ],
                                    }
                                  )(
                                    <Input
                                      disabled={!isEditable || isDisabled}
                                      placeholder={this.props.i18n.t("NamespaceWizardConfig:Drug name")}
                                      allowClear
                                    />
                                  )}
                                </Form.Item>
                              </div>
                            </Col>
                          </Row>

                          <Row gutter={8}>
                            <Col sm={24}>
                              <div style={{ margin: "0 0 10px" }}>
                                {getFieldDecorator(
                                  `drugDosageGroup[${key}].dose_calculation_active`,
                                  {
                                    initialValue: get(
                                      this.state,
                                      `drugDosageSettingsValues[${key}].dose_calculation_active`,
                                      false
                                    ),
                                    valuePropName: "checked",
                                  }
                                )(
                                  <Checkbox
                                    disabled={!isEditable || isDisabled}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {this.props.i18n.t(
                                        "NamespaceWizardConfig:Dose calculation active"
                                      )}
                                    </span>
                                  </Checkbox>
                                )}
                              </div>
                            </Col>
                          </Row>

                          {getFieldValue(
                            `drugDosageGroup[${key}].dose_calculation_active`
                          ) && (
                            <div>
                              <Row gutter={8}>
                                <Col lg={8}>
                                  <Form.Item
                                    label={this.props.i18n.t("NamespaceWizardConfig:Drug state")}
                                  >
                                    {getFieldDecorator(
                                      `drugDosageGroup[${key}].drug_state`,
                                      {
                                        rules: [
                                          {
                                            required: true,
                                            message: this.props.i18n.t("Required"),
                                          },
                                        ],
                                        initialValue: get(
                                          this.state,
                                          `drugDosageSettingsValues[${key}].drug_state`,
                                          null
                                        ),
                                      }
                                    )(
                                      <Select
                                        placeholder={this.props.i18n.t(
                                          "Select"
                                        )}
                                        disabled={!isEditable || isDisabled}
                                        onChange={() => {
                                          this.props.form.setFields({
                                            [`drugDosageGroup[${key}].drug_administration_type`]: {
                                              value: undefined,
                                            },
                                          });
                                          // this.props.form.resetFields([
                                          //   `drugDosageGroup[${key}].drug_administration_type`,
                                          // ]);
                                        }}
                                      >
                                        <Option value="liquid_vial">
                                          {this.props.i18n.t("NamespaceWizardConfig:Liquid/vial")}
                                        </Option>
                                        <Option value="solid_tablet">
                                          {this.props.i18n.t("NamespaceWizardConfig:Solid/Tablet")}
                                        </Option>
                                      </Select>
                                    )}
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Row gutter={8}>
                                <Col lg={8}>
                                  <Form.Item
                                    label={this.props.i18n.t(
                                      "NamespaceWizardConfig:Drug administration type"
                                    )}
                                  >
                                    {getFieldDecorator(
                                      `drugDosageGroup[${key}].drug_administration_type`,
                                      {
                                        rules: [
                                          {
                                            required: true,
                                            message: this.props.i18n.t("Required"),
                                          },
                                        ],
                                        initialValue: get(
                                          this.state,
                                          `drugDosageSettingsValues[${key}].drug_administration_type`,
                                          null
                                        ),
                                      }
                                    )(
                                      <Select
                                        placeholder={this.props.i18n.t(
                                          "Select"
                                        )}
                                        disabled={!isEditable || isDisabled}
                                      >
                                        {getFieldValue(
                                          `drugDosageGroup[${key}].drug_state`
                                        ) === "liquid_vial" && (
                                          <Option value="bolus">{this.props.i18n.t("NamespaceWizardConfig:Bolus")}</Option>
                                        )}
                                        {getFieldValue(
                                          `drugDosageGroup[${key}].drug_state`
                                        ) === "liquid_vial" && (
                                          <Option value="infusion">
                                            {this.props.i18n.t("NamespaceWizardConfig:Infusion")}
                                          </Option>
                                        )}
                                        {getFieldValue(
                                          `drugDosageGroup[${key}].drug_state`
                                        ) === "solid_tablet" && (
                                          <Option value="tablet">{this.props.i18n.t("NamespaceWizardConfig:Tablet")}</Option>
                                        )}
                                      </Select>
                                    )}
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Row gutter={8}>
                                <Col sm={24}>
                                  <div style={{ margin: "0 0 10px" }}>
                                    {getFieldDecorator(
                                      `drugDosageGroup[${key}].calculate_based_on_weight`,
                                      {
                                        initialValue: get(
                                          this.state,
                                          `drugDosageSettingsValues[${key}].calculate_based_on_weight`,
                                          false
                                        ),
                                        valuePropName: "checked",
                                      }
                                    )(
                                      <Checkbox
                                        disabled={!isEditable || isDisabled}
                                      >
                                        <span
                                          style={{
                                            fontSize: "16px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {this.props.i18n.t(
                                            "NamespaceWizardConfig:Calculate based on weight"
                                          )}
                                        </span>
                                      </Checkbox>
                                    )}
                                  </div>
                                </Col>
                              </Row>

                              {getFieldValue(
                                `drugDosageGroup[${key}].drug_administration_type`
                              ) === "bolus" &&
                                getFieldValue(
                                  `drugDosageGroup[${key}].calculate_based_on_weight`
                                ) && (
                                  <Row gutter={8}>
                                    <Col lg={8}>
                                      <div>
                                        <Form.Item
                                          label={this.props.i18n.t(
                                            "NamespaceWizardConfig:Bolus dose mg per kg"
                                          )}
                                        >
                                          {getFieldDecorator(
                                            `drugDosageGroup[${key}].bolus_dose_mg_per_kg`,
                                            {
                                              initialValue: get(
                                                this.state,
                                                `drugDosageSettingsValues[${key}].bolus_dose_mg_per_kg`,
                                                null
                                              ),
                                              rules: [
                                                {
                                                  required: true,
                                                  message: this.props.i18n.t("Required"),
                                                },
                                              ],
                                            }
                                          )(
                                            <InputNumber
                                              disabled={
                                                !isEditable || isDisabled
                                              }
                                              placeholder={this.props.i18n.t(
                                                "NamespaceWizardConfig:Bolus dose mg per kg"
                                              )}
                                              allowClear
                                            />
                                          )}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                  </Row>
                                )}

                              {getFieldValue(
                                `drugDosageGroup[${key}].drug_administration_type`
                              ) === "infusion" &&
                                getFieldValue(
                                  `drugDosageGroup[${key}].calculate_based_on_weight`
                                ) && (
                                  <Row gutter={8}>
                                    <Col lg={8}>
                                      <div>
                                        <Form.Item
                                          label={this.props.i18n.t(
                                            "NamespaceWizardConfig:Infusion dose ml per kg"
                                          )}
                                        >
                                          {getFieldDecorator(
                                            `drugDosageGroup[${key}].infusion_dose_ml_per_kg`,
                                            {
                                              initialValue: get(
                                                this.state,
                                                `drugDosageSettingsValues[${key}].infusion_dose_ml_per_kg`,
                                                null
                                              ),
                                              rules: [
                                                {
                                                  required: true,
                                                  message: this.props.i18n.t("Required"),
                                                },
                                              ],
                                            }
                                          )(
                                            <InputNumber
                                              disabled={
                                                !isEditable || isDisabled
                                              }
                                              placeholder={this.props.i18n.t(
                                                "NamespaceWizardConfig:Infusion dose ml per kg"
                                              )}
                                              allowClear
                                            />
                                          )}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                  </Row>
                                )}

                              {getFieldValue(
                                `drugDosageGroup[${key}].calculate_based_on_weight`
                              ) && (
                                <React.Fragment>
                                  {getFieldValue(
                                    `drugDosageGroup[${key}].drug_state`
                                  ) === "solid_tablet" && (
                                    <Row gutter={8}>
                                      <Col lg={8}>
                                        <div>
                                          <Form.Item
                                            label={this.props.i18n.t(
                                              "NamespaceWizardConfig:Dosage amount per kg"
                                            )}
                                          >
                                            {getFieldDecorator(
                                              `drugDosageGroup[${key}].dosage_amount_per_kg`,
                                              {
                                                initialValue: get(
                                                  this.state,
                                                  `drugDosageSettingsValues[${key}].dosage_amount_per_kg`,
                                                  null
                                                ),
                                                rules: [
                                                  {
                                                    required: true,
                                                    message: this.props.i18n.t("Required"),
                                                  },
                                                ],
                                              }
                                            )(
                                              <Input
                                                disabled={
                                                  !isEditable || isDisabled
                                                }
                                                placeholder={this.props.i18n.t(
                                                  "NamespaceWizardConfig:Dosage amount per kg"
                                                )}
                                                allowClear
                                                addonAfter={
                                                  getFieldValue(
                                                    `drugDosageGroup[${key}].drug_administration_type`
                                                  ) === "infusion"
                                                    ? this.props.i18n.t("ml")
                                                    : this.props.i18n.t("mg")
                                                }
                                              />
                                            )}
                                          </Form.Item>
                                        </div>
                                      </Col>
                                    </Row>
                                  )}
                                  <Row gutter={8}>
                                    <Col lg={8}>
                                      <div>
                                        <Form.Item
                                          label={this.props.i18n.t(
                                            "NamespaceWizardConfig:Minimum dosage weight"
                                          )}
                                        >
                                          {getFieldDecorator(
                                            `drugDosageGroup[${key}].minimum_dosage_weight`,
                                            {
                                              initialValue: get(
                                                this.state,
                                                `drugDosageSettingsValues[${key}].minimum_dosage_weight`,
                                                null
                                              ),
                                            }
                                          )(
                                            <Input
                                              addonAfter="kg"
                                              disabled={
                                                !isEditable || isDisabled
                                              }
                                              placeholder={this.props.i18n.t(
                                                "NamespaceWizardConfig:Minimum dosage weight"
                                              )}
                                              allowClear
                                            />
                                          )}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                    <Col lg={8}>
                                      <div>
                                        <Form.Item
                                          label={this.props.i18n.t(
                                            "NamespaceWizardConfig:Minimum dosage amount"
                                          )}
                                        >
                                          {getFieldDecorator(
                                            `drugDosageGroup[${key}].minimum_dosage_amount`,
                                            {
                                              initialValue: get(
                                                this.state,
                                                `drugDosageSettingsValues[${key}].minimum_dosage_amount`,
                                                null
                                              ),
                                            }
                                          )(
                                            <Input
                                              disabled={
                                                !isEditable || isDisabled
                                              }
                                              placeholder={this.props.i18n.t(
                                                "Minimum dosage amount"
                                              )}
                                              allowClear
                                              addonAfter={
                                                getFieldValue(
                                                  `drugDosageGroup[${key}].drug_administration_type`
                                                ) === "infusion"
                                                  ? this.props.i18n.t("ml")
                                                  : this.props.i18n.t("mg")
                                              }
                                            />
                                          )}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                  </Row>
                                  <Row gutter={8}>
                                    <Col lg={8}>
                                      <div>
                                        <Form.Item
                                          label={this.props.i18n.t(
                                            "NamespaceWizardConfig:Maximum dosage weight"
                                          )}
                                        >
                                          {getFieldDecorator(
                                            `drugDosageGroup[${key}].maximum_dosage_weight`,
                                            {
                                              initialValue: get(
                                                this.state,
                                                `drugDosageSettingsValues[${key}].maximum_dosage_weight`,
                                                null
                                              ),
                                            }
                                          )(
                                            <Input
                                              addonAfter="kg"
                                              disabled={
                                                !isEditable || isDisabled
                                              }
                                              placeholder={this.props.i18n.t(
                                                "NamespaceWizardConfig:Maximum dosage weight"
                                              )}
                                              allowClear
                                            />
                                          )}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                    <Col lg={8}>
                                      <div>
                                        <Form.Item
                                          label={this.props.i18n.t(
                                            "NamespaceWizardConfig:Maximum dosage amount"
                                          )}
                                        >
                                          {getFieldDecorator(
                                            `drugDosageGroup[${key}].maximum_dosage_amount`,
                                            {
                                              initialValue: get(
                                                this.state,
                                                `drugDosageSettingsValues[${key}].maximum_dosage_amount`,
                                                null
                                              ),
                                            }
                                          )(
                                            <Input
                                              disabled={
                                                !isEditable || isDisabled
                                              }
                                              placeholder={this.props.i18n.t(
                                                "NamespaceWizardConfig:Maximum dosage amount"
                                              )}
                                              allowClear
                                              addonAfter={
                                                getFieldValue(
                                                  `drugDosageGroup[${key}].drug_administration_type`
                                                ) === "infusion"
                                                  ? this.props.i18n.t("ml")
                                                  : this.props.i18n.t("mg")
                                              }
                                            />
                                          )}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                  </Row>
                                </React.Fragment>
                              )}

                              {!getFieldValue(
                                `drugDosageGroup[${key}].calculate_based_on_weight`
                              ) && (
                                <Row gutter={8}>
                                  <Col lg={8}>
                                    <div>
                                      <Form.Item
                                        label={this.props.i18n.t(
                                          "NamespaceWizardConfig:Fixed dose amount"
                                        )}
                                      >
                                        {getFieldDecorator(
                                          `drugDosageGroup[${key}].fixed_dose_amount`,
                                          {
                                            initialValue: get(
                                              this.state,
                                              `drugDosageSettingsValues[${key}].fixed_dose_amount`,
                                              null
                                            ),
                                            rules: [
                                              {
                                                required: true,
                                                message: this.props.i18n.t("Required"),
                                              },
                                            ],
                                          }
                                        )(
                                          <Input
                                            disabled={!isEditable || isDisabled}
                                            placeholder={this.props.i18n.t(
                                              "NamespaceWizardConfig:Fixed dose amount"
                                            )}
                                            allowClear
                                            addonAfter={
                                              getFieldValue(
                                                `drugDosageGroup[${key}].drug_administration_type`
                                              ) === "infusion"
                                                ? this.props.i18n.t("ml")
                                                : this.props.i18n.t("mg")
                                            }
                                          />
                                        )}
                                      </Form.Item>
                                    </div>
                                  </Col>
                                </Row>
                              )}

                              {getFieldValue(
                                `drugDosageGroup[${key}].drug_state`
                              ) === "solid_tablet" && (
                                <React.Fragment>
                                  <Row gutter={8}>
                                    <Col lg={8}>
                                      <div>
                                        <Form.Item
                                          label={this.props.i18n.t(
                                            "NamespaceWizardConfig:Active ingredient per time unit (hours)"
                                          )}
                                        >
                                          {getFieldDecorator(
                                            `drugDosageGroup[${key}].active_ingredient_interval_hours`,
                                            {
                                              initialValue: get(
                                                this.state,
                                                `drugDosageSettingsValues[${key}].active_ingredient_interval_hours`,
                                                null
                                              ),
                                            }
                                          )(
                                            <InputNumber
                                              disabled={
                                                !isEditable || isDisabled
                                              }
                                              placeholder={this.props.i18n.t(
                                                "NamespaceWizardConfig:Active ingredient per time unit (hours)"
                                              )}
                                              allowClear
                                            />
                                          )}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                  </Row>
                                  <Row gutter={8}>
                                    <Col sm={24}>
                                      <div style={{ margin: "0 0 10px" }}>
                                        {getFieldDecorator(
                                          `drugDosageGroup[${key}].calculate_based_on_time`,
                                          {
                                            initialValue: get(
                                              this.state,
                                              `drugDosageSettingsValues[${key}].calculate_based_on_time`,
                                              false
                                            ),
                                            valuePropName: "checked",
                                          }
                                        )(
                                          <Checkbox
                                            disabled={!isEditable || isDisabled}
                                          >
                                            <span
                                              style={{
                                                fontSize: "16px",
                                                fontWeight: 500,
                                              }}
                                            >
                                              {this.props.i18n.t(
                                                "NamespaceWizardConfig:Calculate based on time"
                                              )}
                                            </span>
                                          </Checkbox>
                                        )}
                                      </div>
                                    </Col>
                                  </Row>
                                </React.Fragment>
                              )}

                              {getFieldValue(
                                `drugDosageGroup[${key}].calculate_based_on_time`
                              ) && (
                                <Row gutter={8}>
                                  <Col lg={8}>
                                    <div>
                                      <Form.Item
                                        label={this.props.i18n.t(
                                          "NamespaceWizardConfig:Repeat frequency hours"
                                        )}
                                      >
                                        {getFieldDecorator(
                                          `drugDosageGroup[${key}].repeat_frequency_hours`,
                                          {
                                            initialValue: get(
                                              this.state,
                                              `drugDosageSettingsValues[${key}].repeat_frequency_hours`,
                                              null
                                            ),
                                          }
                                        )(
                                          <InputNumber
                                            disabled={!isEditable || isDisabled}
                                            placeholder={this.props.i18n.t(
                                              "NamespaceWizardConfig:Repeat frequency hours"
                                            )}
                                            allowClear
                                          />
                                        )}
                                      </Form.Item>
                                    </div>
                                  </Col>
                                  <Col lg={8}>
                                    <div>
                                      <Form.Item
                                        label={this.props.i18n.t(
                                          "NamespaceWizardConfig:Interval repeat times"
                                        )}
                                      >
                                        {getFieldDecorator(
                                          `drugDosageGroup[${key}].interval_repeat_times`,
                                          {
                                            initialValue: get(
                                              this.state,
                                              `drugDosageSettingsValues[${key}].interval_repeat_times`,
                                              1
                                            ),
                                          }
                                        )(
                                          <InputNumber
                                            disabled={!isEditable || isDisabled}
                                            placeholder={this.props.i18n.t(
                                              "NamespaceWizardConfig:Interval repeat times"
                                            )}
                                            allowClear
                                          />
                                        )}
                                      </Form.Item>
                                    </div>
                                  </Col>
                                </Row>
                              )}

                              <Row gutter={8}>
                                <Col lg={16}>
                                  <div>
                                    <Form.Item
                                      label={this.props.i18n.t(
                                        "NamespaceWizardConfig:Additional instructions"
                                      )}
                                    >
                                      {getFieldDecorator(
                                        `drugDosageGroup[${key}].additional_instructions`,
                                        {
                                          initialValue: get(
                                            this.state,
                                            `drugDosageSettingsValues[${key}].additional_instructions`,
                                            null
                                          ),
                                        }
                                      )(
                                        <Input.TextArea
                                          disabled={!isEditable || isDisabled}
                                          placeholder={this.props.i18n.t("NamespaceWizardConfig:Additional instructions")}
                                          allowClear
                                        />
                                      )}
                                    </Form.Item>
                                  </div>
                                </Col>
                              </Row>

                              <Row gutter={8}>
                                <Col lg={16}>
                                  <Alert
                                    type="info"
                                    message={
                                      <div>
                                        <b>
                                          {this.props.i18n.t("NamespaceWizardConfig:Administration instructions summary")}
                                        </b>
                                        <p>
                                          <i>
                                            {this.props.i18n.t("NamespaceWizardConfig:Example case of these settings ran on a patient of the weight 80kg")}
                                          </i>
                                        </p>

                                        {this.getAdministrationSummary(key)}
                                      </div>
                                    }
                                  />
                                </Col>
                              </Row>
                            </div>
                          )}
                        </div>
                      ) : null
                    )}

                    {drugDosageSettingsKeys.filter((item) => item).length <
                      20 && (
                      <p className={classes.addDrugDossageWrapper}>
                        <Button
                          disabled={!isEditable || isDisabled}
                          type="button"
                          size="small"
                          style={{ padding: "0.3rem" }}
                          onClick={this.addDrugDosageGroup}
                        >
                          {this.props.i18n.t("NamespaceWizardConfig:Add Drug Management Settings")}
                        </Button>
                      </p>
                    )}
                  </div>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col sm={24}>
                  <div className={classes.treatmentGroup}>
                    <h5 className="headline-default">
                      {this.props.i18n.t("NamespaceWizardConfig:Drug Shipment Locations in this order")}) &nbsp; &nbsp;&nbsp;</h5>
                    <div style={{margin: "18px 0 20px"}}>
                      {getFieldDecorator("manufacturer_site_active", {
                        initialValue: get(
                          configData,
                          "manufacturer_site_active",
                          false
                        ),
                        valuePropName: "checked",
                      })(
                        <Checkbox disabled={!isEditable || isDisabled}>
                          <span style={{fontSize: "15px"}}>
                            {this.props.i18n.t("Manufacturer")}
                          </span>
                        </Checkbox>
                      )}
                    </div>
                    <div style={{margin: "18px 0 20px"}}>
                      {getFieldDecorator("depot_site_active", {
                        initialValue: get(
                          configData,
                          "depot_site_active",
                          false
                        ),
                        valuePropName: "checked",
                      })(
                        <Checkbox disabled={!isEditable || isDisabled}>
                          <span style={{fontSize: "15px"}}>
                            {this.props.i18n.t("Depot")}
                          </span>
                        </Checkbox>
                      )}
                    </div>
                    <div style={{margin: "18px 0 20px"}}>
                      {getFieldDecorator("pharmacy_site_active", {
                        initialValue: get(
                          configData,
                          "pharmacy_site_active",
                          false
                        ),
                        valuePropName: "checked",
                      })(
                        <Checkbox disabled={!isEditable || isDisabled}>
                          <span style={{fontSize: "15px"}}>
                            {this.props.i18n.t("Pharmacy")}
                          </span>
                        </Checkbox>
                      )}
                    </div>
                    <div style={{margin: "18px 0 20px"}}>
                      {getFieldDecorator("fridge_site_active", {
                        initialValue: get(
                          configData,
                          "fridge_site_active",
                          false
                        ),
                        valuePropName: "checked",
                      })(
                        <Checkbox disabled={true}>
                          <span style={{fontSize: "15px"}}>
                            {this.props.i18n.t("Fridge")}
                          </span>
                        </Checkbox>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              {drugUploadLocation === drugAvailableAtLocation && (
                <p>
                  <i>{this.props.i18n.t("NamespaceWizardConfig:The drug gets uploaded to ")} "{drugUploadLocation}" {this.props.i18n.t("NamespaceWizardConfig: and becomes available for randomization after review.")}.</i>
                </p>
              )}
              {drugUploadLocation !== drugAvailableAtLocation && (
                <p>
                  <i>{this.props.i18n.t("NamespaceWizardConfig:The drug gets uploaded to ")} "{drugUploadLocation}" {this.props.i18n.t("NamespaceWizardConfig:then gets shipped through the other locations in order and becomes available for randomization in the ")} "{drugAvailableAtLocation}".</i>
                </p>

              )}
            </div>

            <Row gutter={8}>
              {errorMessage && (
                <Col span={24}>
                  <Alert message={errorMessage} type="error"/>
                </Col>
              )}
              <Col span={24}>
              <Button
                  disabled={!isEditable || isDisabled}
                  type="primary"
                  size="large"
                  htmlType="submit"
                >
                  {this.props.i18n.t("Save")}
                  {isLoading && <Icon type="loading"/>}
                </Button>
              </Col>
            </Row>
          </Form>
        ) : (
          <Icon type="loading"/>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});
const mapDispatchToProps = { setAppSettings, logout };

export default Form.create({ name: "randomizationConfig" })(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withNamespaces()(DrugManagementConfigForm))
);



// WEBPACK FOOTER //
// ./src/containers/WizardConfig/DrugManagementConfigForm.js